import Cookies from 'universal-cookie';

import { getPrimaryAvsBrunixNode } from '../utils/meshBrunixIp';
import { getOwnExternalIp } from '../utils/ownExternalIp';
import { resolveRagServer } from '../utils/resolveRagServer';
import appConfig from '../configs/appConfig.json';

// Diagnostic-only: logs when resolveRagServer() picked the local path
// (anything other than the plain remote rag_server) — the remote branch
// always returns appConfig.rag_server verbatim, so anything else means the
// local branch fired, whether it built a URL from the resolved LAN local_ip
// or fell back to plain rag_server_local.
// TEMP-BRUNIX-LOGS: remove once destination resolution is confirmed working.
const logIfLocal = (ragServer: string, brunixIp: string, ownIp: string, localIp: string): void => {
  if (ragServer === appConfig.rag_server) return;
  console.log('[brunixStream] Redirigiendo a rag_server_local:', ragServer, '| IP local (LAN) del nodo Brunix:', localIp || 'unknown', '| brunixIp:', brunixIp, '| ownIp:', ownIp);
};

const _cookies = new Cookies();
const getAuthToken = (): string => {
  const user = _cookies.get('101Obex');
  return user?.token ?? '';
};
const getDevToken = (): string => {
  const user = _cookies.get('101Obex');
  return user?.dev_token ?? '';
};

// getPrimaryAvsBrunixIp() returns a human-readable 'unknown (...)' string
// when it can't resolve anything (not logged in yet, no persisted edge,
// etc.) — only a real address is worth forwarding to the proxy as
// X-Target-Ip; sending the 'unknown (...)' text itself would just make the
// proxy try to resolve that as a hostname.
const targetIpHeader = (ip: string): Record<string, string> =>
  ip && !ip.startsWith('unknown') ? { 'X-Target-Ip': ip } : {};

export interface ParamResolutionEntry {
  step: number;
  tool: string;
  param: string;
  value: any;
  status: 'certain' | 'inferred' | 'runtime' | 'default' | 'missing';
  source: string;
  question?: string;
}

export interface ParamResolution {
  resolved: ParamResolutionEntry[];
  missing: ParamResolutionEntry[];
  runtime: string[];
}

export interface MissingQuestion {
  step_idx: number;
  tool: string;
  param: string;
  question: string;
  label?: string;
  required: boolean;
}

export interface ImplicitCreate {
  step_idx: number;
  tool: string;
  label: string;
  condition: string;
}

export interface PlanStep {
  tool: string;
  reason: string;
  args?: Record<string, any>;
  node_type: 'check' | 'conditional' | 'followup' | 'action';
  condition: string;
}

export interface ConfirmationPayload {
  plan: PlanStep[];
  message: string;
  summary?: string;
  param_resolution?: ParamResolution;
  missing?: MissingQuestion[];
  implicit_creates?: ImplicitCreate[];
  type?: string;
  entity?: string;
  team_context?: Record<string, any>;
  optional?: MissingQuestion[];
}

export interface ToolResult {
  tool: string;
  output: any;
  status: number;
  error?: string;
}

export interface StepProgress {
  step: number;
  total: number;
  tool: string;
  status: 'start' | 'ok' | 'error';
  output?: any;
  args?: any;
}

export interface TaskGraphEntry {
  task: string;
  type: 'RETRIEVAL' | 'CODE_GENERATION' | 'CONVERSATIONAL' | 'PLATFORM' | 'STATUS' | 'API_ACTION';
  depends_on: number[];
  action?: string;
  resource?: string;
  slots?: Record<string, any>;
}

export interface StreamPart {
  type: 'step' | 'text' | 'confirmation' | 'inform' | 'tool_result' | 'step_progress' | 'task_graph' | 'classify_result' | 'plan_ready' | 'tools_loaded' | 'playbook_found' | 'template_found' | 'generating_response' | 'cancelled';
  content: string;
  message?: string;
  summary?: string;
  runId?: string;
  confirmationPayload?: ConfirmationPayload;
  confirmed?: boolean | null;
  plan?: any[];
  toolResults?: ToolResult[];
  stepProgress?: StepProgress;
  paramResolution?: ParamResolution;
  missing?: MissingQuestion[];
  taskGraph?: TaskGraphEntry[];
  classifyIntent?: string;
  classifyMethod?: string;
  processId?: string;
  toolsCount?: number;
  planSteps?: any[];
  templateTools?: string[];
  stepData?: any;
  pipelineSteps?: { node: string;[k: string]: any }[];
}

export const USE_LOCAL_BRUNIX = true;
export const CONFIRM_AS_MODAL = false;

export const sendConfirmStream = async (
  runId: string,
  confirmed: boolean,
  sessionId: string,
  onStep: (step: string, message: string) => void,
  onText: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
  onToolResult?: (results: ToolResult[]) => void,
  onStepProgress?: (progress: StepProgress) => void,
  signal?: AbortSignal,
  filledSlots?: Record<string, Record<string, any>>,
  selectedSteps?: number[],
  onConfirmation?: (runId: string, payload: ConfirmationPayload) => void,
  onLlmCall?: (status: 'start' | 'done', node: string) => void,
  retryArgs?: any,
  skipStep?: boolean,
  onPipelineStep?: (step: { node: string;[k: string]: any }) => void,
): Promise<void> => {
  try {
    const body: Record<string, any> = {
      run_id: runId,
      confirmed,
      session_id: sessionId,
      token: getAuthToken(),
    };
    const slots: Record<string, any> = { ...(filledSlots ?? {}) };
    if (retryArgs !== undefined) slots['__retry__'] = retryArgs;
    if (skipStep) slots['__skip__'] = true;
    if (Object.keys(slots).length > 0) {
      body.filled_slots = JSON.stringify(slots);
    }
    if (selectedSteps && selectedSteps.length > 0) {
      body.selected_steps = JSON.stringify(selectedSteps);
    }
    const [brunixNode, ownIp] = await Promise.all([getPrimaryAvsBrunixNode(), getOwnExternalIp()]);
    const { ip: brunixIp, localIp } = brunixNode;
    const ragServer = resolveRagServer(brunixIp, ownIp, localIp);
    // TEMP-BRUNIX-LOGS
    console.log('[brunixStream] IP del servidor Brunix conectado al AVS Principal:', brunixIp, '| IP externa del frontal:', ownIp, '| rag_server usado:', ragServer);
    logIfLocal(ragServer, brunixIp, ownIp, localIp);
    const res = await fetch(`${ragServer}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...targetIpHeader(brunixIp) },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) { onError('Confirm failed: ' + res.status); return; }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) { onDone(); break; }
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        const t = line.trim();
        if (!t) continue;
        try {
          const msg = JSON.parse(t);
          if (msg.error) { onError(msg.error); return; }
          if (msg.llm_call) {
            onLlmCall?.(msg.status as 'start' | 'done', msg.node ?? '');
          } else if (msg.avap_pipeline_step) {
            onPipelineStep?.({ node: msg.node, ...msg });
          } else if (msg.step_progress) {
            if (msg.step_progress.tool === '__pipeline__' && msg.step_progress.output?.node) {
              onPipelineStep?.({ node: msg.step_progress.output.node, ...msg.step_progress.output });
            } else {
              onStepProgress?.(msg.step_progress as StepProgress);
            }
          } else if (msg.tool_results) {
            onToolResult?.(msg.results ?? []);
          } else if (msg.confirmation && msg.runId) {
            // Nested confirmation — guard detected a missing prerequisite (e.g. no team exists)
            // and is asking the user to approve a fallback plan before continuing.
            onConfirmation?.(msg.runId, msg.confirmationPayload ?? { plan: [], message: '', summary: '' });
            // confirmation_payload and is_final arrive in the same message — call onDone too.
            if (msg.is_final) onDone();
          } else if (msg.step) {
            onStep(msg.step, msg.message ?? '');
          } else if (msg.text !== undefined) {
            onText(msg.text);
            if (msg.is_final) onDone();
          } else if (msg.is_final) {
            onDone();
          }
        } catch { /* skip malformed */ }
      }
    }
  } catch (err) {
    if (err?.name !== 'AbortError') onError(err?.message ?? 'Confirm stream error');
  }
};

export const confirmAction = async (
  runId: string,
  confirmed: boolean,
  sessionId: string,
  filledSlots?: Record<string, Record<string, any>>,
): Promise<void> => {
  try {
    const body: Record<string, any> = { run_id: runId, confirmed, session_id: sessionId };
    if (filledSlots && Object.keys(filledSlots).length > 0) {
      body.filled_slots = JSON.stringify(filledSlots);
    }
    const [brunixNode, ownIp] = await Promise.all([getPrimaryAvsBrunixNode(), getOwnExternalIp()]);
    const { ip: brunixIp, localIp } = brunixNode;
    const ragServer = resolveRagServer(brunixIp, ownIp, localIp);
    // TEMP-BRUNIX-LOGS
    console.log('[brunixStream] IP del servidor Brunix conectado al AVS Principal:', brunixIp, '| IP externa del frontal:', ownIp, '| rag_server usado:', ragServer);
    logIfLocal(ragServer, brunixIp, ownIp, localIp);
    await fetch(`${ragServer}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...targetIpHeader(brunixIp) },
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.error('confirmAction error:', err?.message);
  }
};

export const sendGrpcStream = async (
  query: string,
  sessionId: string,
  onStep: (step: string, message: string) => void,
  onText: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
  signal?: AbortSignal,
  onConfirmation?: (runId: string, payload: ConfirmationPayload) => void,
  onInform?: (plan: any[], message: string, summary: string, paramResolution?: ParamResolution, missing?: MissingQuestion[]) => void,
  onStepProgress?: (progress: StepProgress) => void,
  extraContext?: string,
  mcpServers?: { type: string; name: string; url: string; bearer_token?: string }[],
  orgId?: string | number,
  projectId?: string | number,
  onTaskGraph?: (tasks: TaskGraphEntry[]) => void,
  onClassify?: (intent: string, method: string) => void,
  onPlanReady?: (processId: string, steps: any[], total: number) => void,
  onLlmCall?: (status: 'start' | 'done', node: string) => void,
  onToolsLoaded?: (count: number) => void,
  onPlaybookFound?: (processId: string) => void,
  onTemplateFound?: (processId: string, tools: string[]) => void,
  onAbort?: () => void,
): Promise<void> => {
  try {
    const body: Record<string, any> = { query, session_id: sessionId, token: getAuthToken() };
    if (orgId) body.org_id = String(orgId);
    if (projectId) body.project_id = String(projectId);
    if (extraContext) body.extra_context = extraContext;
    if (mcpServers && mcpServers.length > 0) body.mcp_servers = mcpServers;
    const devToken = getDevToken();
    if (devToken) body.developer_token = devToken;
    const [brunixNode, ownIp] = await Promise.all([getPrimaryAvsBrunixNode(), getOwnExternalIp()]);
    const { ip: brunixIp, localIp } = brunixNode;
    const ragServer = resolveRagServer(brunixIp, ownIp, localIp);
    // TEMP-BRUNIX-LOGS
    console.log('[brunixStream] IP del servidor Brunix conectado al AVS Principal:', brunixIp, '| IP externa del frontal:', ownIp, '| rag_server usado:', ragServer);
    logIfLocal(ragServer, brunixIp, ownIp, localIp);
    const res = await fetch(`${ragServer}/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...targetIpHeader(brunixIp) },
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok || !res.body) { onError('Proxy connection failed: ' + res.status); return; }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) { onDone(); break; }
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        const t = line.trim();
        if (!t) continue;
        try {
          const msg = JSON.parse(t);
          if (msg.error) { onError(msg.error); return; }
          if (msg.llm_call) {
            onLlmCall?.(msg.status as 'start' | 'done', msg.node ?? '');
          } else if (msg.tools_loaded) {
            onToolsLoaded?.(msg.count ?? 0);
          } else if (msg.playbook_found) {
            onPlaybookFound?.(msg.process_id ?? '');
          } else if (msg.template_found) {
            onTemplateFound?.(msg.process_id ?? '', msg.tools ?? []);
          } else if (msg.classify_result) {
            onClassify?.(msg.intent ?? '', msg.method ?? 'rule');
          } else if (msg.task_graph) {
            onTaskGraph?.(msg.tasks ?? []);
          } else if (msg.plan_ready) {
            onPlanReady?.(msg.process_id ?? '', msg.steps ?? [], msg.total ?? 0);
          } else if (msg.plan_preview) {
            // legacy alias — skip duplicate card
          } else if (msg.step_progress) {
            onStepProgress?.(msg.step_progress as StepProgress);
          } else if (msg.planning) {
            // planning event — plan_action node has started generating the plan
            if (msg.status === 'start') onStep('Planner', 'Generating action plan...');
          } else if (msg.inform) {
            onInform?.(
              msg.plan ?? [],
              msg.message ?? '',
              msg.summary ?? '',
              msg.param_resolution ?? undefined,
              msg.missing ?? undefined,
            );
          } else if (msg.confirmation && msg.runId) {
            onConfirmation?.(msg.runId, msg.confirmationPayload ?? { plan: [], message: '', summary: '' });
            // confirmation_payload and is_final arrive in the same message — call onDone too.
            if (msg.is_final) onDone();
          } else if (msg.step) {
            try {
              console.log('[sendGrpcStream] step type:', typeof msg.step, '| value:', msg.step);
              const stepStr = typeof msg.step === 'string' ? msg.step : JSON.stringify(msg.step);
              onStep(stepStr, msg.message ?? '');
            } catch (err) {
              console.error('[sendGrpcStream] error in onStep:', err, '| raw msg:', msg);
            }
          } else if (msg.text !== undefined) {
            onText(msg.text);
            if (msg.is_final) onDone();
          } else if (msg.is_final) {
            onDone();
          }
        } catch { /* skip malformed */ }
      }
    }
  } catch (err) {
    if (err?.name === 'AbortError') onAbort?.();
    else onError(err?.message ?? 'Connection error');
  }
};
